import React, { useState, useCallback } from 'react';
import { FiArrowLeft, FiUpload, FiAlertCircle, FiCheck, FiInfo } from 'react-icons/fi';
import { useAuth } from '../../../contexts/AuthContext';

interface CsvRow {
  [key: string]: string;
}

interface RowError {
  row: number;
  reason: string;
  data: any;
}

interface SummaryStepProps {
  rows: CsvRow[];
  mapping: Record<string, string>;
  errors: RowError[];
  duplicates: number[];
  onImport: (batchId: string) => void;
  onBack: () => void;
  onClose: () => void;
}

export const SummaryStep: React.FC<SummaryStepProps> = ({
  rows,
  mapping,
  errors,
  duplicates,
  onImport,
  onBack,
  onClose
}) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const validRows = rows.length - errors.length;
  const hasErrors = errors.length > 0;
  const hasDuplicates = duplicates.length > 0;

  const handleImport = useCallback(async () => {
    setIsImporting(true);
    setImportError(null);

    try {
      // Create FormData with CSV content
      const csvContent = generateCSVContent(rows, mapping);
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const file = new File([blob], 'customers.csv', { type: 'text/csv' });
      
      const formData = new FormData();
      formData.append('file', file);

      if (!currentBusiness) {
        throw new Error('No business selected');
      }

      const response = await fetch(`${API_BASE_URL}/batch/customers/import`, {
        method: 'POST',
        // Do NOT set Content-Type when sending FormData; the browser will set the correct multipart boundary
        headers: {
          ...(isCookieMode ? {} : { Authorization: `Bearer ${token}` }),
        },
        credentials: isCookieMode ? 'include' : 'omit',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to start import');
      }

      const result = await response.json();
      onImport(result.batchId);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Import failed';
      setImportError(errorMessage);
    } finally {
      setIsImporting(false);
    }
  }, [rows, mapping, onImport, currentBusiness, token, isCookieMode, API_BASE_URL]);

  const generateCSVContent = (rows: CsvRow[], mapping: Record<string, string>): string => {
    // Get the mapped headers in the correct order
    const headers = Object.keys(mapping);
    const csvHeaders = headers.map(key => mapping[key]);
    
    // Create CSV content
    const csvRows = [csvHeaders.join(',')];
    
    rows.forEach(row => {
      const values = headers.map(key => {
        const value = row[mapping[key]] || '';
        // Escape commas and quotes
        return `"${value.replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });
    
    return csvRows.join('\n');
  };

  return (
    <div className="p-6 pb-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Ready to Import
          </h3>
          <p className="text-neutral-600 dark:text-neutral-400">
            Review the summary below and click Import to start the customer import process.
          </p>
        </div>

        {/* Import Summary */}
        <div className="bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700 p-6 mb-6">
          <h4 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-4">
            Import Summary
          </h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Total Rows:</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-50">{rows.length}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Valid Rows:</span>
                <span className="font-medium text-green-600 dark:text-green-400">{validRows}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Errors:</span>
                <span className="font-medium text-red-600 dark:text-red-400">{errors.length}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Duplicates:</span>
                <span className="font-medium text-yellow-600 dark:text-yellow-400">{duplicates.length}</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Display Name:</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-50">
                  {mapping.display_name || 'Not mapped'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Email:</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-50">
                  {mapping.email || 'Not mapped'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Phone:</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-50">
                  {mapping.phone || 'Not mapped'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 dark:text-neutral-400">Address:</span>
                <span className="font-medium text-neutral-900 dark:text-neutral-50">
                  {mapping.billing_addr_line1 ? 'Mapped' : 'Not mapped'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Warnings */}
        {(hasErrors || hasDuplicates) && (
          <div className="mb-6 space-y-4">
            {hasErrors && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <div className="flex items-start">
                  <FiAlertCircle className="h-5 w-5 text-yellow-500 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                      Import Warning
                    </h4>
                    <p className="text-sm text-yellow-700 dark:text-yellow-300">
                      {errors.length} rows have validation errors and will be skipped during import. 
                      Only {validRows} valid rows will be imported.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {hasDuplicates && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start">
                  <FiInfo className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                      Duplicate Detection
                    </h4>
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      {duplicates.length} rows appear to be duplicates and will be skipped during import.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Import Process Info */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-6">
          <h4 className="text-sm font-medium text-neutral-900 dark:text-neutral-50 mb-2">
            Import Process
          </h4>
          <ul className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
            <li>• Import will be processed in the background</li>
            <li>• You can continue using the application while importing</li>
            <li>• Progress will be shown in a dialog</li>
            <li>• You can undo the import within 60 minutes</li>
            <li>• Invalid rows will be skipped automatically</li>
          </ul>
        </div>

        {/* Error Display */}
        {importError && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex items-center">
              <FiAlertCircle className="h-5 w-5 text-red-500 mr-2" />
              <p className="text-red-700 dark:text-red-300">{importError}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
          >
            <FiArrowLeft className="mr-2" size={16} />
            Back
          </button>
          
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || validRows === 0}
              className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isImporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Starting Import...
                </>
              ) : (
                <>
                  <FiUpload className="mr-2" size={16} />
                  Import {validRows} Customers
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 